import { Check } from 'lucide-react';

interface MarkReadButtonProps {
  onClick: () => void;
  /** Accessible name + tooltip. Callers pass the translated string. */
  label: string;
  className?: string;
}

/**
 * The per-card "mark as read" affordance.
 *
 * Replaces the bare muted `×` the activity cards used to share. Two problems
 * with that glyph: at `text-muted-foreground` it was the same weight as the
 * timestamp beside it, and an × reads as "delete this" rather than "I'm done
 * with this" — so people either missed it or didn't trust it. A bordered check
 * in brand-primary says both things at once.
 *
 * `btn-outline` is required, not decorative: Ionic's button reset beats
 * Tailwind preflight on `<button>`, so `border-brand-primary` alone resolves to
 * `border-style: none` and the ring disappears (see utils.scss).
 *
 * The fill comes from `.hp-mark-read-btn`, not a `bg-brand-primary/10` utility:
 * `brand-*` are hand-written classes in utils.scss, not Tailwind theme colors,
 * so Tailwind generates nothing for the `/10` opacity modifier. (The `×` this
 * replaced carried a `hover:bg-brand-danger/10` that never rendered for the
 * same reason — part of why it read as so faint.)
 */
export const MarkReadButton: React.FC<MarkReadButtonProps> = ({
  onClick,
  label,
  className,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className={[
      'hp-mark-read-btn flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full btn-outline transition-colors active:opacity-80',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <Check className="h-4 w-4" strokeWidth={2.5} />
  </button>
);
